// src/components/AdminPages/DashboardPage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
const API = process.env.REACT_APP_API_URL;
const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalSales: 0,
    ordersToday: 0,
    lowStockItems: 0,
    upcomingProducts: 0
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);

  // ── Data fetching ────────────────────────────────────────
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [
          statsRes,
          ordersRes,
          lowStockRes,
          salesRes,
          usersRes,
          topProductsRes,
          orderBreakdownRes
        ] = await Promise.all([
          axios.get(`${API}/api/dashboard/stats`),
          axios.get(`${API}/api/dashboard/orders/recent`),
          axios.get(`${API}/api/dashboard/products/low-stock`),
          axios.get(`${API}/api/dashboard/sales/week`),
          axios.get(`${API}/api/dashboard/users/growth-week`),
          axios.get(`${API}/api/dashboard/products/top-selling`),
          axios.get(`${API}/api/dashboard/orders/status-breakdown`),
        ]);

        setStats(statsRes.data);
        setRecentOrders(ordersRes.data);
        setLowStockProducts(lowStockRes.data);
        setSalesData(salesRes.data);
        setUserGrowthData(usersRes.data);
        setTopProductsData(topProductsRes.data);
        setOrderStatusData(orderBreakdownRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboard();
  }, []);

  const STATUS_COLORS = {
    placed: "#60a5fa",
    packed: "#facc15",
    shipped: "#38bdf8",
    "out for delivery": "#a78bfa",
    delivered: "#22c55e",
    cancelled: "#ef4444"
  };

  const ORDER_STATUSES = [
    "placed", "packed", "shipped", "out for delivery", "delivered", "cancelled"
  ];

  const legendData = ORDER_STATUSES.map(status => {
    const found = orderStatusData.find(item => item.status === status);
    return {
      status,
      count: found ? found.count : 0
    };
  });

  const salesDays = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// Suppose API returns: [{ day: "Sunday", sales: 200 }, { day: "Monday", sales: 0 }, ...]
const filledSalesData = salesDays.map(day => {
  const data = salesData.find(s => s.day === day);
  return { day, sales: data ? Number(data.sales) : 0 };
});


  return (
   <div className="p-3 xs:p-4 sm:p-5 md:p-6 lg:p-8 max-[360px]:px-2 max-[360px]:py-2 w-full max-w-7xl min-h-screen bg-gray-50/40 overflow-x-hidden">
      <h1 className="text-lg xs:text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 xs:mb-5 sm:mb-6 md:mb-8 break-words">
        Dashboard Overview
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 md:gap-6 lg:gap-6 mb-5 xs:mb-6 sm:mb-8 md:mb-10 w-full min-w-0">
        {/* Total Sales */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-3 min-w-0 w-[320px] [@media(min-width:361px)]:w-full">

          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Total Sales (This Month)</p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mt-1.5 break-words">
                ₹{(stats.totalSales ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-green-100 p-2.5 sm:p-3 rounded-full flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        {/* Orders Today */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-3 xs:p-4 sm:p-5 md:p-6 w-full min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Orders Today</p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mt-1.5">{stats.ordersToday}</p>
            </div>
            <div className="bg-blue-100 p-2.5 sm:p-3 rounded-full flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Low Stock Items */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-3 xs:p-4 sm:p-5 md:p-6 min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Low Stock Items</p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-orange-600 mt-1.5">{stats.lowStockItems}</p>
            </div>
            <div className="bg-orange-100 p-2.5 sm:p-3 rounded-full flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Upcoming Products */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-3 xs:p-4 sm:p-5 md:p-6  min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Upcoming Products</p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-purple-600 mt-1.5">{stats.upcomingProducts}</p>
            </div>
            <div className="bg-purple-100 p-2.5 sm:p-3 rounded-full flex-shrink-0">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Sales + Low Stock Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-6 mb-6 sm:mb-8 md:mb-10 w-full min-w-0">
        {/* Sales This Week */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 md:p-6  min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Sales This Week</h2>
          <div className="h-64 xs:h-72 sm:h-80 md:h-[22rem] lg:h-80 xl:h-96 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filledSalesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#6b7280" tick={{ fontSize: 10 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => `₹${Number(value || 0).toLocaleString()}`} contentStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="sales" stroke="#10b981" strokeWidth={2} dot={{ r: 4, fill: "#10b981" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 md:p-6  min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">Low Stock Alerts</h2>
            <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-1 rounded">Urgent</span>
          </div>
          <ul className="space-y-2 sm:space-y-3 text-sm max-h-64 overflow-y-auto">
            {lowStockProducts.slice(0, 6).map((item, idx) => (
              <li key={idx} className="flex justify-between items-center gap-3">
                <span className="text-gray-700 truncate flex-1">{item.name}</span>
                <span className={`font-medium whitespace-nowrap ${item.stock <= 3 ? "text-red-600" : "text-orange-800"}`}>
                  {item.stock}
                </span>
              </li>
            ))}
          </ul>
          <button
            className="mt-4 w-full bg-orange-600 hover:bg-orange-700 text-white py-2.5 sm:py-3 rounded-lg text-sm font-medium transition-colors"
            onClick={() => setShowInventoryModal(true)}
          >
            View All Low Stock Items
          </button>
        </div>
      </div>

      {/* New Users + Top Selling Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 md:gap-7 mb-6 sm:mb-8 md:mb-10 w-full min-w-0">
        {/* New Users */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 md:p-6 min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-4 sm:mb-5">New Users This Week</h2>
          <div className="h-64 xs:h-72 sm:h-80 md:h-[22rem] lg:h-80 xl:h-96 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => `${value} users`} contentStyle={{ fontSize: '13px' }} />
                <Line type="monotone" dataKey="newUsers" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4, fill: "#8b5cf6" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Selling Products */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 md:p-6  min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-4 sm:mb-5">Top Selling Products</h2>
          <div className="h-64 xs:h-72 sm:h-80 md:h-[22rem] lg:h-80 xl:h-96 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProductsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 10 }}
                  interval={0}
                  stroke="#6b7280"
                />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => `${value} units`} contentStyle={{ fontSize: '13px' }} />
                <Bar dataKey="units" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5 md:p-6 mb-6 sm:mb-8 md:mb-10  min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-4 sm:mb-5">
          Order Status Breakdown
        </h2>

        <div className=" md:items-center  md:gap-10 w-full min-w-0 flex flex-col md:flex-row items-center gap-6">
          {/* Legend */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 min-w-[180px] md:min-w-[220px]">
            {legendData.map((item, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-2.5 ${item.count === 0 ? "opacity-50" : ""}`}
              >
                <span
                  className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: STATUS_COLORS[item.status] || "#d1d5db" }}
                />
                <span className="text-sm text-gray-700 capitalize">
                  {item.status} ({item.count})
                </span>
              </div>
            ))}
          </div>

          {/* Pie Chart */}
          <div className="w-full h-64 sm:h-72 md:h-80 lg:h-96 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={orderStatusData}
                  dataKey="count"
                  nameKey="status"
                                    cx="50%"
                  cy="50%"
                  outerRadius={
                    window.innerWidth < 480
                      ? 70
                      : window.innerWidth < 640
                      ? 90
                      : window.innerWidth < 768
                      ? 100
                      : 110
                  }
                  innerRadius={window.innerWidth < 480 ? 30 : 40}
                  label={false}
                  
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || "#d1d5db"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '13px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6 sm:mb-8 md:mb-10 min-w-0 w-[320px] [@media(min-width:361px)]:w-full">
        <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5 border-b border-gray-200">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">Recent Orders</h2>
        </div>

        {/* Mobile card layout */}
        <div className="sm:hidden divide-y divide-gray-200">
          {recentOrders.map((order) => (
            <div key={order.id} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium text-blue-600 truncate">{order.id}</div>
                  <div className="text-sm text-gray-600 mt-0.5 truncate">{order.customer}</div>
                </div>
                <span
                  className={`px-1.5 py-1 text-xs font-semibold rounded-full ${
                    order.status === "Delivered" ? "bg-green-100 text-green-800" :
                    order.status === "Shipped" ? "bg-blue-100 text-blue-800" :
                    order.status === "Pending Verification" ? "bg-orange-100 text-orange-800" :
                    "bg-indigo-100 text-indigo-800"
                  }`}
                >
                  {order.status}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="font-medium">₹{order.total}</span>
                <span className="text-gray-500 text-xs">
                  {new Date(order.time).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
            </div>
          ))}
        </div>
        {/* Desktop/table view */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full min-w-[360px] sm:min-w-[640px] text-sm">

            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-5 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-blue-600 truncate">{order.id}</td>
                  <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 truncate max-w-[140px] md:max-w-none">{order.customer}</td>
                  <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900">₹{order.total}</td>
                  <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap">
                    <span className={`px-2.5 sm:px-3 py-1 inline-flex text-xs font-semibold rounded-full leading-5 ${
                      order.status === "Delivered" ? "bg-green-100 text-green-800" :
                      order.status === "Shipped" ? "bg-blue-100 text-blue-800" :
                      order.status === "Pending Verification" ? "bg-orange-100 text-orange-800" :
                      "bg-indigo-100 text-indigo-800"
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 sm:px-5 md:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                    {new Date(order.time).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inventory Modal */}
      {showInventoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg sm:max-w-xl md:max-w-2xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-5 md:p-6 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
                Low Stock Inventory
              </h3>
              <button
                onClick={() => setShowInventoryModal(false)}
                className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Inventory list */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10 shadow-sm">
                  <tr>
                    <th className="text-left py-3 font-medium text-gray-700">Product</th>
                    <th className="text-right py-3 font-medium text-gray-700">Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lowStockProducts.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="py-3 pr-4 truncate">{item.name}</td>
                      <td
                        className={`py-3 text-right font-medium ${
                          item.stock <= 3 ? "text-red-600" : "text-orange-600"
                        }`}
                      >
                        {item.stock}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 md:p-6 border-t border-gray-200 text-right">
              <button
                onClick={() => setShowInventoryModal(false)}
                className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm sm:text-base font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;

